import { Controller, isBackend } from "remult";

import {
  Client,
  PlaceAutocompleteType,
  Status,
  type PlaceAutocompleteRequest,
  type PlaceDetailsRequest,
  type LatLng,
  TravelMode,
  Language,
} from "@googlemaps/google-maps-services-js";
import { geocode } from "@googlemaps/google-maps-services-js/dist/geocode/geocode";
import { placeAutocomplete } from "@googlemaps/google-maps-services-js/dist/places/autocomplete";
import { placeDetails } from "@googlemaps/google-maps-services-js/dist/places/details";
import { directions } from "@googlemaps/google-maps-services-js/dist/directions";
import { HyperBackendMethod } from "@shared/backend/hyper-backend-method";
import { Roles } from "@shared/Roles";
import { resolvePlacesAutocompleteMode } from "@shared/helpers/google-places-autocomplete";
import axios from "axios";

@Controller("google-places")
export class GooglePlacesController {
  private static API_KEY = isBackend()
    ? process.env.GOOGLE_API_KEY || "AIzaSyCcZKioWQ2bCyGlTETmi531icopaOrSZE8"
    : "";

  private static _client: Client;

  private static get client() {
    if (!this._client) {
      this._client = new Client({
        axiosInstance: axios,
      });
    }

    return this._client;
  }

  @HyperBackendMethod({
    allowed: true,
    overrideSecurity: true,
  })
  static async getAutocomplete(
    input: string,
    options: Partial<
      Omit<
        PlaceAutocompleteRequest["params"],
        "input" | "types" | "components" | "key"
      > & { country?: string; includeEstablishments?: boolean; }
    > = {},
  ) {
    const {
      language = "he",
      country = "IL",
      includeEstablishments = false,
      ...rest
    } = options;

    const countryFilter =
      country.toUpperCase() === "IL"
        ? "country:il|country:ps"
        : `country:${country}`;

    const apiKey =
      process.env.GOOGLE_API_KEY ||
      "AIzaSyCcZKioWQ2bCyGlTETmi531icopaOrSZE8";

    const { types } = resolvePlacesAutocompleteMode(input, {
      includeEstablishments,
    });

    const { data: predictions } = await placeAutocomplete({
      params: {
        ...rest,
        key: apiKey,
        input,
        ...(types ? { types: types as PlaceAutocompleteType } : {}),
        language,
        components: [countryFilter],
      },
    });

    if (predictions.status !== Status.OK) {
      if (predictions.status === Status.ZERO_RESULTS) {
        return [];
      }
      throw new Error(`Google Places API error: ${predictions.error_message}`);
    }

    return predictions.predictions;
  }

  @HyperBackendMethod({
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async geocodeAddress(address: string): Promise<{
    lat: number;
    lng: number;
    formattedAddress: string;
  } | null> {
    const trimmed = address.trim();
    if (!trimmed) return null;

    const addressWithCountry = trimmed.includes("ישראל")
      ? trimmed
      : `${trimmed}, ישראל`;

    const { data } = await geocode({
      params: {
        address: addressWithCountry,
        language: "he",
        key:
          process.env.GOOGLE_API_KEY ||
          "AIzaSyCcZKioWQ2bCyGlTETmi531icopaOrSZE8",
        components: "country:IL|country:PS",
        region: "il",
      },
      timeout: 10000,
    });

    if (data.status !== Status.OK || !data.results?.[0]?.geometry?.location) {
      return null;
    }

    const { lat, lng } = data.results[0].geometry.location;
    return {
      lat,
      lng,
      formattedAddress: data.results[0].formatted_address ?? trimmed,
    };
  }

  @HyperBackendMethod({
    allowed: true,
    overrideSecurity: true,
  })
  static async getPlaceCoordinates(
    placeId: string,
    options: Partial<
      Omit<PlaceDetailsRequest["params"], "place_id" | "key" | "fields">
    > = {},
  ) {
    const { data } = await placeDetails({
      params: {
        ...options,
        key:
          process.env.GOOGLE_API_KEY ||
          "AIzaSyCcZKioWQ2bCyGlTETmi531icopaOrSZE8",
        place_id: placeId,
        fields: ["geometry"], // Only fetch geometry to reduce data size
      },
    });

    if (!data.result?.geometry?.location) {
      console.error("Error in getting place coordinates:", data.error_message);
      return null;
    }

    return data.result.geometry.location;
  }

  /**
   * Optimizes a route by rearranging waypoints in the most efficient order.
   * Uses Google Directions API with optimize:true to calculate the optimal route.
   *
   * @param origin - Starting point (can be address, coordinates, or place_id)
   * @param destination - End point (can be same as origin for round trip)
   * @param waypoints - Array of intermediate stops (minimum 3 required for optimization)
   * @param options - Additional options like travel mode, language, etc.
   * @returns Optimized route with waypoint order, total distance, duration, and full route details
   *
   * @example
   * ```ts
   * const result = await GooglePlacesController.optimizeRoute(
   *   "תל אביב",
   *   "תל אביב",
   *   ["כתובת1", "כתובת2", "כתובת3"]
   * );
   * // result.waypointOrder: [2, 0, 1] - optimal order to visit waypoints
   * ```
   */
  @HyperBackendMethod({
    allowed: true,
    overrideSecurity: true,
  })
  static async optimizeRoute(
    origin: string | LatLng,
    destination: string | LatLng,
    waypoints: (string | LatLng)[],
    options: {
      mode?: TravelMode;
      language?: Language | string;
      region?: string;
      departureTime?: Date | number | "now";
    } = {},
  ) {
    if (waypoints.length < 3) {
      throw new Error(
        "Route optimization requires at least 3 waypoints. For fewer waypoints, use regular directions API.",
      );
    }

    const { mode = TravelMode.driving, language = Language.iw, region = "IL", departureTime } = options;

    const { data } = await directions({
      params: {
        key:
          process.env.GOOGLE_API_KEY ||
          "AIzaSyCcZKioWQ2bCyGlTETmi531icopaOrSZE8",
        origin,
        destination,
        waypoints,
        optimize: true, // Enable route optimization
        mode,
        language: typeof language === "string" ? (language as Language) : language,
        region,
        ...(departureTime && { departure_time: departureTime }),
      },
    });

    if (data.status !== Status.OK) {
      const errorMessage = data.error_message || data.status;

      // Check for common API errors
      if (data.status === "REQUEST_DENIED") {
        if (errorMessage?.includes("legacy API") || errorMessage?.includes("not enabled")) {
          throw new Error(
            "Directions API לא מופעל בפרויקט. יש להפעיל את Directions API ב-Google Cloud Console. " +
            "למידע נוסף: https://console.cloud.google.com/apis/library/directions-backend.googleapis.com"
          );
        }
        throw new Error(
          `Google Directions API נדחה: ${errorMessage}. בדוק שהמפתח תקין והשירות מופעל.`
        );
      }

      throw new Error(
        `Google Directions API error: ${errorMessage}`,
      );
    }

    if (!data.routes || data.routes.length === 0) {
      throw new Error("No routes found");
    }

    const route = data.routes[0];
    const optimizedWaypointOrder = route.waypoint_order || [];

    // Calculate total distance and duration
    let totalDistance = 0;
    let totalDuration = 0;
    let totalDurationInTraffic = 0;

    route.legs.forEach((leg) => {
      totalDistance += leg.distance.value;
      totalDuration += leg.duration.value;
      if (leg.duration_in_traffic) {
        totalDurationInTraffic += leg.duration_in_traffic.value;
      }
    });

    // Format total distance text
    const totalDistanceKm = (totalDistance / 1000).toFixed(1);
    const totalDistanceText = `${totalDistanceKm} ק"מ`;

    return {
      waypointOrder: optimizedWaypointOrder, // The optimal order to visit waypoints
      totalDistance: {
        value: totalDistance, // in meters
        text: totalDistanceText,
      },
      totalDuration: {
        value: totalDuration, // in seconds
        text: formatDuration(totalDuration),
      },
      totalDurationInTraffic: totalDurationInTraffic
        ? {
          value: totalDurationInTraffic, // in seconds
          text: formatDuration(totalDurationInTraffic),
        }
        : undefined,
      route: route, // Full route details including turn-by-turn navigation
      geocodedWaypoints: data.geocoded_waypoints, // Geocoding details for each waypoint
    };
  }
}

/**
 * Formats duration in seconds to a human-readable string
 */
function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours} שעות ${minutes} דקות`;
  }
  return `${minutes} דקות`;
}
